/**
 * Apple Pay Button for Web
 *
 * Uses Apple Pay JS SDK 1.2.0+ which provides the <apple-pay-button> custom element.
 * This element works across ALL browsers:
 * - Safari: Native Apple Pay flow
 * - Chrome/Firefox/Edge: SDK shows QR code for iOS 18+ users to scan
 *
 * CRITICAL: ApplePaySession must be created synchronously within a user gesture handler.
 * Payment request is prefetched on mount so session creation is instant on click.
 *
 * @see https://developer.apple.com/documentation/applepayontheweb
 * @see https://developer.apple.com/documentation/apple_pay_on_the_web/displaying_apple_pay_buttons_using_javascript
 */
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {ActivityIndicator, Platform, Pressable, StyleSheet, View} from 'react-native';

import theme from '../../../../../style/theme';
import Text from '../../../../themed/Text';

import {applePayService} from './applePayService';
import type {ApplePayMerchantSession, ApplePayPayment, ApplePayPaymentRequest} from './types';
import {useApplePayAvailability} from './useApplePayAvailability';
import {useApplePaySession} from './useApplePaySession';

interface ApplePayButtonWebProps {
  /** Apple Pay service ID from usePaymentServices */
  serviceId: string;
  /** Remaining balance in cents after gift cards (for split payment) */
  remainingBalanceCents?: number;
  /** Callback when payment is authorized */
  onPaymentAuthorized: (payment: ApplePayPayment) => Promise<boolean>;
  /** Callback when payment is cancelled */
  onCancel?: () => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Button style variant */
  buttonStyle?: 'black' | 'white' | 'white-outline';
  /** Button type - determines the button label text */
  buttonType?: 'plain' | 'buy' | 'pay' | 'set-up' | 'donate' | 'check-out' | 'book' | 'subscribe';
  /** Whether button is disabled */
  disabled?: boolean;
}

/**
 * Apple Pay button component for web.
 *
 * IMPORTANT: ApplePaySession must be created synchronously within a user gesture handler.
 * We prefetch the payment request on mount so the session can be created instantly on click.
 *
 * @example
 * ```tsx
 * <ApplePayButton
 *   serviceId={applePayServiceId}
 *   onPaymentAuthorized={async (payment) => {
 *     const result = await processPaymentWithTill(payment.token);
 *     return result.success;
 *   }}
 *   buttonStyle="black"
 *   buttonType="pay"
 * />
 * ```
 */
export function ApplePayButton({
  serviceId,
  remainingBalanceCents,
  onPaymentAuthorized,
  onCancel,
  onError,
  buttonStyle = 'black',
  buttonType = 'pay',
  disabled = false,
}: ApplePayButtonWebProps) {
  const buttonRef = useRef<HTMLDivElement>(null);

  // Prefetched payment request - fetched on mount, used on click
  const [paymentRequest, setPaymentRequest] = useState<ApplePayPaymentRequest | null>(null);
  const [isPrefetching, setIsPrefetching] = useState(true);
  const [prefetchError, setPrefetchError] = useState<string | null>(null);

  // Check availability (includes third-party browser support)
  const availability = useApplePayAvailability({
    skip: Platform.OS !== 'web',
  });

  // Keep the latest onError in a ref so the prefetch effect below does NOT list
  // it as a dependency. The checkout config factory (createApplePayCheckoutConfigs)
  // allocates a fresh onError closure on every parent re-render; without this the
  // prefetch effect re-fires on each render and floods
  // GET /me/payment-services/:id/tokenization. Mirrors handleClickRef in
  // GooglePayButton.web.tsx.
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  // Merchant validation handler
  const handleMerchantValidation = useCallback(
    async (validationURL: string): Promise<ApplePayMerchantSession> => {
      return applePayService.validateMerchant(validationURL);
    },
    [],
  );

  // Session management
  const {state, startSession} = useApplePaySession({
    onMerchantValidation: handleMerchantValidation,
    onPaymentAuthorized,
    onCancel,
    onError,
  });

  // Prefetch payment request on mount
  // This ensures we have the config ready for instant session creation on click
  useEffect(() => {
    let cancelled = false;

    const prefetch = async () => {
      setIsPrefetching(true);
      setPrefetchError(null);

      try {
        const request = await applePayService.getPaymentRequest(serviceId, remainingBalanceCents);
        if (!cancelled) {
          setPaymentRequest(request);
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : 'Failed to load payment config';
          setPrefetchError(message);
          onErrorRef.current?.(error instanceof Error ? error : new Error(message));
        }
      } finally {
        if (!cancelled) {
          setIsPrefetching(false);
        }
      }
    };

    if (serviceId && availability.canMakePayments) {
      prefetch();
    } else {
      setIsPrefetching(false);
    }

    return () => {
      cancelled = true;
    };
    // onError intentionally excluded — read via onErrorRef to keep this effect
    // from re-firing (and re-hitting tokenization) on every parent re-render.
  }, [serviceId, remainingBalanceCents, availability.canMakePayments]);

  // CRITICAL: This handler must be synchronous to maintain user gesture context
  // ApplePaySession creation must happen immediately within the click handler
  const handlePress = useCallback(() => {
    if (disabled || state.isActive || isPrefetching || !availability.canMakePayments) {
      return;
    }

    if (!paymentRequest) {
      onError?.(new Error('Payment configuration not loaded. Please try again.'));
      return;
    }

    // Start session SYNCHRONOUSLY - no async operations before this
    startSession(paymentRequest);
  }, [
    disabled,
    state.isActive,
    isPrefetching,
    availability.canMakePayments,
    paymentRequest,
    startSession,
    onError,
  ]);

  // Retry prefetch on error
  const handleRetry = useCallback(async () => {
    setIsPrefetching(true);
    setPrefetchError(null);

    try {
      const request = await applePayService.getPaymentRequest(serviceId, remainingBalanceCents);
      setPaymentRequest(request);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load payment config';
      setPrefetchError(message);
    } finally {
      setIsPrefetching(false);
    }
  }, [serviceId, remainingBalanceCents]);

  // Attach click handler to the apple-pay-button element
  useEffect(() => {
    const button = buttonRef.current?.querySelector('apple-pay-button');
    if (button) {
      const clickHandler = () => handlePress();
      button.addEventListener('click', clickHandler);
      return () => button.removeEventListener('click', clickHandler);
    }
  }, [handlePress]);

  // Don't render if Apple Pay not supported
  if (!availability.isSupported || !availability.canMakePayments) {
    return null;
  }

  // Show loading during prefetch or session
  if (isPrefetching || state.isActive) {
    const loadingText = isPrefetching
      ? 'Loading...'
      : state.isValidating
        ? 'Verifying merchant...'
        : 'Processing payment...';

    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="small" color={theme.colour.uiControls?.primary ?? '#0054A6'} />
        <Text style={styles.loadingText}>{loadingText}</Text>
      </View>
    );
  }

  // Show prefetch error state
  if (prefetchError) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>{prefetchError}</Text>
        <Pressable style={styles.retryButton} onPress={handleRetry}>
          <Text style={styles.retryButtonText}>Try Again</Text>
        </Pressable>
      </View>
    );
  }

  // Show session error state
  if (state.error) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>{state.error}</Text>
        <Pressable style={styles.retryButton} onPress={handlePress}>
          <Text style={styles.retryButtonText}>Try Again</Text>
        </Pressable>
      </View>
    );
  }

  // Render Apple Pay button using SDK's custom element
  // The SDK provides <apple-pay-button> which works in all browsers
  // In third-party browsers, clicking it triggers the QR code flow automatically
  return (
    <View style={styles.container}>
      <div ref={buttonRef} style={{width: '100%'}}>
        {/*
          Apple Pay SDK custom element - works in ALL browsers
          SDK handles button rendering and QR code flow for third-party browsers
          @see https://developer.apple.com/documentation/apple_pay_on_the_web/displaying_apple_pay_buttons_using_javascript
        */}
        <apple-pay-button
          buttonstyle={buttonStyle}
          type={buttonType}
          locale="en-AU"
          style={
            {
              '--apple-pay-button-width': '100%',
              '--apple-pay-button-height': '44px',
              '--apple-pay-button-border-radius': '8px',
              '--apple-pay-button-padding': '0',
              '--apple-pay-button-box-sizing': 'border-box',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.5 : 1,
            } as React.CSSProperties
          }
        />
      </div>

      {/* Show hint for third-party browser users */}
      {availability.isThirdPartyBrowser ? (
        <Text style={styles.qrHintText}>Scan QR code with iPhone (iOS 18+) to pay</Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    backgroundColor: '#F5F5F5',
    borderRadius: 8,
  },
  loadingText: {
    marginLeft: 8,
    fontSize: 14,
    color: '#666',
  },
  errorContainer: {
    padding: 12,
    backgroundColor: '#FEE2E2',
    borderRadius: 8,
    alignItems: 'center',
  },
  errorText: {
    fontSize: 14,
    color: '#DC2626',
    marginBottom: 8,
  },
  retryButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#DC2626',
    borderRadius: 4,
  },
  retryButtonText: {
    color: '#FFF',
    fontSize: 14,
    fontWeight: '600',
  },
  qrHintText: {
    marginTop: 8,
    fontSize: 12,
    color: '#666',
    textAlign: 'center',
  },
});

export default ApplePayButton;
